import React from "react";
import {
  Building2, Phone, Mail, MapPin, ShieldCheck, Globe2, Users2, Trophy,
  Briefcase, Cpu, Car, Landmark, ShoppingBag, Pill, Heart, Baby, Plane, Palette,
} from "lucide-react";

const STATS = [
  { v: "20+", l: "Years", h: "DMC craftsmanship since 2005" },
  { v: "140K+", l: "Travellers", h: "Curated leisure & MICE pax" },
  { v: "07", l: "Destinations", h: "Asia & Middle East" },
  { v: "24/7", l: "Concierge", h: "Global ops desk" },
  { v: "500+", l: "MICE Groups", h: "Delivered end-to-end" },
  { v: "70%", l: "MICE Volume", h: "Of total program mix" },
];

const DESTINATIONS = [
  { city: "Dubai", country: "UAE", tagline: "Where Ambition Meets Desert", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=900" },
  { city: "Singapore", country: "Singapore", tagline: "The Lion City Roars", img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=900" },
  { city: "Bangkok", country: "Thailand", tagline: "Land of Smiles", img: "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=900" },
  { city: "Kuala Lumpur", country: "Malaysia", tagline: "Truly Asia", img: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=900" },
  { city: "Hong Kong", country: "SAR", tagline: "Asia's World City", img: "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=900" },
  { city: "Manila", country: "Philippines", tagline: "7,641 Islands", img: "https://images.pexels.com/photos/35649530/pexels-photo-35649530.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { city: "Hanoi", country: "Vietnam", tagline: "Timeless Charm", img: "https://images.pexels.com/photos/30634746/pexels-photo-30634746.jpeg?auto=compress&cs=tinysrgb&w=900" },
];

const MICE_SECTORS = [
  { i: <Briefcase className="w-3.5 h-3.5" />, t: "Fortune 500" },
  { i: <Pill className="w-3.5 h-3.5" />, t: "Pharma" },
  { i: <Cpu className="w-3.5 h-3.5" />, t: "Technology" },
  { i: <Car className="w-3.5 h-3.5" />, t: "Automotive" },
  { i: <Landmark className="w-3.5 h-3.5" />, t: "Financial Services" },
  { i: <ShoppingBag className="w-3.5 h-3.5" />, t: "FMCG" },
];

const LEISURE_SECTORS = [
  { i: <Heart className="w-3.5 h-3.5" />, t: "Honeymoons" },
  { i: <Heart className="w-3.5 h-3.5" />, t: "Weddings" },
  { i: <Baby className="w-3.5 h-3.5" />, t: "Family & Multi-Gen" },
  { i: <Plane className="w-3.5 h-3.5" />, t: "Bespoke FIT" },
  { i: <Palette className="w-3.5 h-3.5" />, t: "Special Interest" },
];

const ACCREDS = [
  { i: <Trophy className="w-4 h-4" />, t: "Est. 2005 · 2 Decades" },
  { i: <Users2 className="w-4 h-4" />, t: "24/7 Global Concierge" },
];

export default function ParentCompanySection() {
  return (
    <section
      id="parent"
      className="relative border-t border-[#1a1a1a] bg-gradient-to-b from-black to-[#080808] py-24"
      data-testid="parent-company-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <p className="overline flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> Parent Company · Trawell DMC
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-white mt-4 leading-[0.98] uppercase">
              Twenty years of moving<br />
              the world, <span className="text-brand">one group at a time.</span>
            </h2>
          </div>
          <div className="md:col-span-5 text-[#a3a3a3] leading-relaxed">
            <p>
              <span className="text-white font-semibold">TrawellB2B</span> is the partner-network platform
              powered by <span className="text-white font-semibold">Trawell DMC</span> — a Dubai-headquartered
              destination management company operating end-to-end across South-East Asia and West Asia.
            </p>
            <p className="mt-3">
              140,000+ travellers curated. 500+ MICE groups delivered. Seven destinations,
              one accountable partner — and now, a global B2B network on top of it.
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STATS.map((s, i) => (
            <div
              key={s.l}
              className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-5 hover:border-brand transition-colors fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`parent-stat-${s.l.toLowerCase()}`}
            >
              <p className="font-display font-black text-3xl md:text-4xl text-white">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand mt-2">{s.l}</p>
              <p className="text-xs text-[#737373] mt-1">{s.h}</p>
            </div>
          ))}
        </div>

        {/* Sectors served — MICE & Leisure (no client name-dropping; under NDA) */}
        <div className="mt-12 pt-10 border-t border-[#1a1a1a]" data-testid="sectors-served-strip">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <p className="overline">Sectors served · Recognised names under NDA</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#737373]">
              {ACCREDS.map((a) => (
                <span key={a.t} className="inline-flex items-center gap-1.5">
                  <span className="text-brand">{a.i}</span>
                  <span className="font-mono uppercase tracking-widest">{a.t}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* MICE rail */}
            <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">MICE · A-list verticals</p>
                <span className="font-mono text-[10px] text-[#737373]">70% volume</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {MICE_SECTORS.map((s) => (
                  <span
                    key={s.t}
                    className="inline-flex items-center gap-1.5 bg-black border border-[#262626] hover:border-brand transition-colors px-3 py-1.5 rounded-full text-xs text-white"
                    data-testid={`mice-sector-${s.t.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="text-brand">{s.i}</span> {s.t}
                  </span>
                ))}
              </div>
            </div>

            {/* Leisure rail */}
            <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Leisure · A-list categories</p>
                <span className="font-mono text-[10px] text-[#737373]">FIT · Group · Bespoke</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {LEISURE_SECTORS.map((s) => (
                  <span
                    key={s.t}
                    className="inline-flex items-center gap-1.5 bg-black border border-[#262626] hover:border-brand transition-colors px-3 py-1.5 rounded-full text-xs text-white"
                    data-testid={`leisure-sector-${s.t.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="text-brand">{s.i}</span> {s.t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Destinations grid */}
        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <p className="overline">Operated end-to-end</p>
              <h3 className="font-display text-3xl md:text-5xl font-black tracking-tighter text-white mt-3 uppercase">
                Seven destinations.<br />
                <span className="text-brand">One trusted operator.</span>
              </h3>
            </div>
            <p className="text-sm text-[#a3a3a3] max-w-md">
              Each destination is staffed by an on-ground team with two decades of relationships
              with hoteliers, transport operators, and venue partners.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="destinations-grid">
            {DESTINATIONS.map((d, i) => (
              <article
                key={d.city}
                className="relative aspect-[3/4] rounded-lg overflow-hidden border border-[#262626] group cursor-pointer fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}
                data-testid={`destination-${d.city.toLowerCase()}`}
              >
                <img
                  src={d.img}
                  alt={d.city}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-brand">{d.country}</p>
                  <h4 className="font-display text-lg font-bold text-white leading-tight mt-1">{d.city}</h4>
                  <p className="text-[10px] text-[#a3a3a3] mt-0.5 line-clamp-1">{d.tagline}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* MICE case study + Contact split */}
        <div className="grid md:grid-cols-12 gap-6 mt-20">
          {/* MICE */}
          <div className="md:col-span-7 relative bg-[#050505] border border-[#262626] rounded-xl p-8 md:p-12 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-brand/10 rounded-full blur-3xl" />
            <p className="overline relative">MICE Specialists · 70% volume</p>
            <h3 className="relative font-display text-3xl md:text-4xl font-bold text-white mt-3 leading-tight">
              From a 40-pax board offsite to a <span className="text-brand">2,500-pax dealer conference</span>.
            </h3>
            <p className="relative text-[#a3a3a3] mt-4 leading-relaxed max-w-xl">
              Venue sourcing, AV production, ground mobility, catering, control-room operations.
              We don't book — we author the program.
            </p>
            <div className="relative grid grid-cols-2 gap-3 mt-7">
              <div className="bg-black border-l-4 border-brand p-4">
                <p className="overline">Case study</p>
                <p className="font-display text-white text-base font-bold mt-1 leading-tight">
                  1,800-pax Global Sales Conference
                </p>
                <p className="font-mono text-xs text-[#737373] mt-1.5">Dubai · 5 days · 11 hotels · April 2024</p>
              </div>
              <div className="bg-black border-l-4 border-[#262626] p-4">
                <p className="overline">Coverage</p>
                <p className="font-display text-white text-base font-bold mt-1 leading-tight">
                  Corporate offsites · Dealer incentives · Conferences · Launches
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <aside className="md:col-span-5 bg-[#0a0a0a] border border-[#262626] rounded-xl p-8 md:p-10">
            <p className="overline">Trawell DMC · Head Office</p>
            <h3 className="font-display text-2xl font-bold text-white mt-3">Talk to a human in 30 seconds.</h3>
            <p className="text-sm text-[#a3a3a3] mt-3">
              Our 24/7 concierge runs out of Dubai with regional operators in Singapore, Bangkok,
              Kuala Lumpur, Hong Kong, Manila and Hanoi.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-3 text-[#e5e5e5]">
                <MapPin className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                <span>Al Barsha, Dubai, United Arab Emirates</span>
              </li>
              <li className="flex items-start gap-3 text-[#e5e5e5]">
                <Phone className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                <span className="font-mono">+971 52 452 6313 · +971 58 179 9090 · +91 77359 19191</span>
              </li>
              <li className="flex items-start gap-3 text-[#e5e5e5]">
                <Mail className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                <a href="mailto:hello@trawelldmc.com" className="hover:text-brand transition-colors">
                  hello@trawelldmc.com
                </a>
              </li>
            </ul>
            <a
              href="https://trawellb2b.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm mt-7"
              data-testid="visit-parent-site"
            >
              Visit trawellb2b.com →
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}
